import { useState, useEffect } from 'react'
import { Form } from 'react-bootstrap'
import { useFormik } from 'formik'
import { Text, Number, Email, TextArea, SelectBox, SelectStatusBox, File } from 'components/input_fields'
import { SaveButton } from 'components/buttons'
import { FindServiceSchema } from './ValidationSchema'
import { getServiceCategoryOptions } from './serviceCategoryOptions'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { premiseList } from '../../../../redux/slices/premises'
import { addFindService } from '../../../../redux/slices/help_desk'
import { toggleAddModal, toggleBool } from '../../../../redux/slices/globalSlice'
import { addUpdateHandler, SessionManager } from 'utils'
import { useTranslation } from 'react-i18next'

export default function AddFindServiceForm() {

    const [spinner, setSpinner] = useState(false)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const premise = useSelector((state) => state?.premise?.list)
    const premiseLoading = useSelector((state) => state?.premise?.loading)
    const user_data = JSON.parse(SessionManager.getCookie('user'))

    const premise_option = premise?.map((val) => ({ value: val?.id, label: val?.name }))
    const category_option = getServiceCategoryOptions(t)

    const initialValues = {
        premise_id: user_data?.premise_id || '',
        service_category: '',
        service_name: '',
        mobile_number: '',
        alternative_contact: '',
        email: '',
        description: '',
        address: '',
        photo: null,
        status: 'Active',
    }

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, setFieldTouched } = useFormik({
        initialValues,
        validationSchema: FindServiceSchema,
        onSubmit: (vals, action) => {
            setSpinner(true)
            dispatch(addFindService({ values: vals })).then((res) => addUpdateHandler({
                res: res.payload,
                action,
                navigate,
                success_toast: `${t('findService.service')} ${t('common.success_add')}`,
                wrong_toast: t('common.wrong'),
                navigate_route: '',
                spinner: setSpinner,
                toggle: () => {
                    dispatch(toggleAddModal())
                    dispatch(toggleBool())
                },
            }))
        },
    })

    useEffect(() => {
        dispatch(premiseList({ navigate }))
    }, [])

    return (
        <Form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
            <div className="container-fluid">
                <div className="row g-2">

                    <div className="col-md-4">
                        <SelectBox
                            label_name={t('findService.premise')}
                            mandatory={true}
                            placeholder=""
                            disabled={user_data?.premise_id}
                            loading={premiseLoading}
                            option={premise_option}
                            name="premise_id"
                            value={values.premise_id}
                            onChange={(e) => setFieldValue('premise_id', e)}
                            onBlur={() => setFieldTouched('premise_id', true)}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            error={errors.premise_id && touched.premise_id ? <span className="field_error">{errors.premise_id}</span> : null}
                        />
                    </div>

                    <div className="col-md-4">
                        <SelectBox
                            label_name={t('findService.service_category')}
                            mandatory={true}
                            placeholder=""
                            option={category_option}
                            name="service_category"
                            value={values.service_category}
                            onChange={(e) => setFieldValue('service_category', e)}
                            onBlur={() => setFieldTouched('service_category', true)}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            error={errors.service_category && touched.service_category ? <span className="field_error">{errors.service_category}</span> : null}
                        />
                    </div>

                    <div className="col-md-4">
                        <Text
                            label_name={t('findService.service_name')}
                            mandatory={true}
                            placeholder=""
                            name="service_name"
                            value={values.service_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.service_name && touched.service_name ? <span className="field_error">{errors.service_name}</span> : null}
                        />
                    </div>

                    <div className="col-md-4">
                        <Number
                            label_name={t('findService.mobile_number')}
                            mandatory={true}
                            placeholder=""
                            name="mobile_number"
                            value={values.mobile_number}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.mobile_number && touched.mobile_number ? <span className="field_error">{errors.mobile_number}</span> : null}
                        />
                    </div>

                    <div className="col-md-4">
                        <Number
                            label_name={t('findService.alternative_contact')}
                            mandatory={false}
                            placeholder=""
                            name="alternative_contact"
                            value={values.alternative_contact}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.alternative_contact && touched.alternative_contact ? <span className="field_error">{errors.alternative_contact}</span> : null}
                        />
                    </div>

                    <div className="col-md-4">
                        <Email
                            label_name={t('findService.email')}
                            mandatory={false}
                            placeholder=""
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.email && touched.email ? <span className="field_error">{errors.email}</span> : null}
                        />
                    </div>

                    <div className="col-md-4">
                        <SelectStatusBox
                            label_name={t('findService.status')}
                            mandatory={true}
                            name="status"
                            value={values.status}
                            onChange={(e) => setFieldValue('status', e)}
                            onBlur={() => setFieldTouched('status', true)}
                            error={errors.status && touched.status ? <span className="field_error">{errors.status}</span> : null}
                        />
                    </div>

                    <div className="col-md-4">
                        <File
                            label_name={t('findService.photo')}
                            mandatory={false}
                            name="photo"
                            accept="image/*"
                            onChange={(e) => setFieldValue('photo', e.target.files?.[0] || null)}
                            onBlur={handleBlur}
                        />
                    </div>

                    <div className="col-md-12">
                        <TextArea
                            label_name={t('findService.address')}
                            mandatory={false}
                            placeholder=""
                            rows={2}
                            name="address"
                            value={values.address}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.address && touched.address ? <span className="field_error">{errors.address}</span> : null}
                        />
                    </div>

                    <div className="col-md-12">
                        <TextArea
                            label_name={t('findService.description')}
                            mandatory={false}
                            placeholder=""
                            rows={2}
                            name="description"
                            value={values.description}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.description && touched.description ? <span className="field_error">{errors.description}</span> : null}
                        />
                    </div>

                    <div className="col-md-12">
                        <div className="d-flex justify-content-end">
                            <div className="col-lg-4">
                                <SaveButton button_name={t('common.submit')} spinner={spinner} />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </Form>
    )
}